import classnames from 'classnames';
import { forwardRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  IconButton,
  CheckboxInput,
  DropdownInput,
  ArrowLeft as leftIcon,
  ArrowRight as rightIcon,
  IcNoDataEvent as noDataEventIcon,
  CalendarIcon as calendarIcon,
} from '@/shared/ui';
import useHrChartOptions from './useHrChartOptions';
import './styles.scss';

export type HrChartOptionsHandle = {
  reload: () => void;
};

export type HrChartOptionsProps = {
  timezoneOffset: number;
  studyId: string;
  profileId: string;
  startReport: any;
  stopReport: any;
};

/**
 * Renders the date navigator and event legend beneath the ECG disclosure chart. Shows a skeleton while the options or the chart are still loading, otherwise renders previous and next day arrows disabled at the ends of dailyCount, a calendar dropdown of the available dates, and the AFib, artifact and no-data legend unless the study has no data.
 *
 * @param props - Study context forwarded to useHrChartOptions.
 * @param ref - Forwarded handle the parent uses to change the selected date imperatively.
 * @returns The date navigator row with its legend, or a loading skeleton.
 */
const HrChartOptions = forwardRef<HrChartOptionsHandle, HrChartOptionsProps>(
  ({ timezoneOffset, studyId, profileId, startReport, stopReport }, ref) => {
    const {
      isLoadingHrChartOptions,
      isLoadingHrChart,
      dateIndex,
      dailyCount,
      dropdownOptions,
      formatSelectedDate,
      onChangeDate,
      handleClickArrow,
      isEmptyData,
    } = useHrChartOptions({ timezoneOffset, studyId, profileId, startReport, stopReport }, ref);

    if (isLoadingHrChartOptions || isLoadingHrChart) {
      return (
        <div className="navigator-skeleton-container">
          <div className="skeleton-container">
            <Skeleton height="100%" />
          </div>
        </div>
      );
    }

    return (
      <div id="hr-chart-options" className="ecg-options">
        <div className="select-date">
          <IconButton
            id="ecg-disclosure-previous-date-btn"
            className="me-2"
            isFilled
            disabled={dateIndex === 0}
            iconComponent={<img src={leftIcon} alt="Left icon" />}
            onClick={() => {
              handleClickArrow(false);
            }}
          />
          <DropdownInput
            dropdownToggleId="ecg-disclosure-date-dropdown-toggle"
            name="date"
            disabled={!dailyCount?.length}
            options={dropdownOptions}
            value={formatSelectedDate}
            onChange={onChangeDate}
            selectedItemIcon={<img src={calendarIcon} alt="Icon calendar" />}
            showCaret={false}
          />
          <IconButton
            id="ecg-disclosure-next-date-btn"
            className="ms-2"
            isFilled
            disabled={dateIndex === (dailyCount?.length ?? 0) - 1}
            iconComponent={<img src={rightIcon} alt="Right icon" />}
            onClick={() => {
              handleClickArrow(true);
            }}
          />
        </div>
        {!isEmptyData && (
          <div className="event-info-container">
            <CheckboxInput
              className={classnames('event-info-checkbox', '--afib', 'always-visible')}
              id="afibEventStripManagement"
              name="afibEvent"
              data={
                {
                  label: 'AFib',
                  value: false, // pre-existing bug: CheckboxInput's `data.value` is typed string|number
                } as any
              }
              checked={false}
              onChange={() => {}}
            />
            <CheckboxInput
              className={classnames('event-info-checkbox', '--artifact', 'always-visible')}
              id="artifactStripManagement"
              name="artifact"
              data={
                {
                  label: 'Artifact',
                  value: false, // pre-existing bug: CheckboxInput's `data.value` is typed string|number
                } as any
              }
              checked={false}
              onChange={() => {}}
            />
            <div className={classnames('event-info-checkbox', '--no-data')}>
              <img src={noDataEventIcon} alt="no-data-event" />
              <div>No data</div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default HrChartOptions;
